import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowUp,
  Loader2,
  LogIn,
  RotateCcw,
  Settings2,
  Sparkles,
  X,
} from "lucide-react";
import Logo from "@/components/common/Logo";
import { CHIPS, getScenario } from "./scenarios";
import PlanView from "./PlanView";
import ResultCard from "./ResultCard";

// In a real MV3 extension this URL comes from chrome.tabs.query({active:true}).
const CURRENT_URL = "app.acme.com/login";
const MODES = ["Cheapest", "Balanced", "Max"];

export default function SidePanel({ onClose }) {
  const [signedIn, setSignedIn] = useState(false);
  const [modeIndex, setModeIndex] = useState(1);

  const [phase, setPhase] = useState("idle"); // idle|planning|running|judging|done
  const [instruction, setInstruction] = useState("");
  const [steps, setSteps] = useState([]);
  const [result, setResult] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [seq, setSeq] = useState(0);
  const [input, setInput] = useState("");

  const bottomRef = useRef(null);

  // Keep the newest content in view as the run progresses.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [phase, steps, result]);

  // Scripted run timeline — advances the plan, then judges, then shows result.
  useEffect(() => {
    if (seq === 0 || !scenario) return undefined;
    const timers = [];
    const stepGap = 700;

    timers.push(setTimeout(() => setPhase("running"), 650));

    scenario.steps.forEach((_, i) => {
      const runningAt = 650 + i * stepGap;
      const doneAt = runningAt + 600;
      timers.push(
        setTimeout(() => {
          setSteps((prev) =>
            prev.map((s, idx) => (idx === i ? { ...s, status: "running" } : s))
          );
        }, runningAt)
      );
      timers.push(
        setTimeout(() => {
          setSteps((prev) =>
            prev.map((s, idx) => {
              if (idx !== i) return s;
              const isFail =
                scenario.verdict === "FAILED" && scenario.failIndex === i;
              return { ...s, status: isFail ? "fail" : "done" };
            })
          );
        }, doneAt)
      );
    });

    const afterSteps = 650 + scenario.steps.length * stepGap;
    timers.push(setTimeout(() => setPhase("judging"), afterSteps));
    timers.push(
      setTimeout(() => {
        setResult({
          verdict: scenario.verdict,
          duration: scenario.duration,
          cost: scenario.cost,
          tokens: scenario.tokens,
          bug: scenario.bug,
          note: scenario.note,
        });
        setPhase("done");
      }, afterSteps + 900)
    );

    return () => timers.forEach(clearTimeout);
  }, [seq, scenario]);

  const busy = phase === "planning" || phase === "running" || phase === "judging";

  function startRun(id, text) {
    const sc = getScenario(id, text);
    setScenario(sc);
    setInstruction(sc.instruction);
    setResult(null);
    setSteps(sc.steps.map((label) => ({ label, status: "pending" })));
    setPhase("planning");
    setInput("");
    setSeq((s) => s + 1);
  }

  function resetRun() {
    setPhase("idle");
    setSteps([]);
    setResult(null);
    setScenario(null);
    setInstruction("");
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!input.trim() || busy) return;
    startRun("default", input.trim());
  }

  // ---- Sign-in gate -------------------------------------------------------
  if (!signedIn) {
    return (
      <PanelShell onClose={onClose}>
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
          <Logo />
          <h2 className="mt-6 text-base font-semibold text-slate-900 dark:text-white">
            Connect your account
          </h2>
          <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
            Sign in to test the page you&apos;re on. Runs save to your team&apos;s
            history.
          </p>
          <button
            onClick={() => setSignedIn(true)}
            className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-brand-600 px-6 text-sm font-semibold text-white shadow-soft transition-colors hover:bg-brand-500"
          >
            <LogIn size={17} /> Sign in
          </button>
          <p className="mt-3 text-[0.6875rem] text-slate-400">
            Runs on AI-QA-Engine cloud · no API key to paste
          </p>
        </div>
      </PanelShell>
    );
  }

  // ---- Main panel ---------------------------------------------------------
  return (
    <PanelShell
      onClose={onClose}
      mode={MODES[modeIndex]}
      onCycleMode={() => setModeIndex((i) => (i + 1) % MODES.length)}
      onReset={phase !== "idle" ? resetRun : null}
    >
      {/* tab being tested */}
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2 text-xs text-slate-500 dark:border-white/5 dark:text-slate-400">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Testing&nbsp;<span className="font-medium text-slate-700 dark:text-slate-200">{CURRENT_URL}</span>
      </div>

      {/* conversation / greeting */}
      <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {phase === "idle" ? (
          <Greeting onPick={(id) => startRun(id)} />
        ) : (
          <>
            {/* user instruction bubble */}
            <div className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-brand-600 px-3.5 py-2.5 text-sm text-white shadow-soft">
                {instruction}
              </div>
            </div>

            {phase === "planning" ? (
              <TypingBubble label="Planning the test…" />
            ) : (
              <PlanView steps={steps} />
            )}

            {phase === "judging" && (
              <div className="flex items-center gap-2 px-1 text-xs text-slate-500 dark:text-slate-400">
                <Loader2 size={13} className="animate-spin text-brand-500" />
                Validating outcome with judge model…
              </div>
            )}

            {phase === "done" && result && <ResultCard result={result} />}
          </>
        )}
        <div ref={bottomRef} />
      </div>

      {/* composer */}
      <div className="border-t border-slate-100 p-3 dark:border-white/5">
        <form
          onSubmit={handleSubmit}
          className="flex items-end gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-500/10 dark:border-white/10 dark:bg-white/5"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask AI-QA-Engine to test something…"
            className="h-7 flex-1 bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none dark:text-white"
          />
          <button
            type="submit"
            disabled={!input.trim() || busy}
            aria-label="Run test"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-brand-600 text-white transition-colors hover:bg-brand-500 disabled:opacity-40"
          >
            {busy ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <ArrowUp size={16} strokeWidth={2.5} />
            )}
          </button>
        </form>

        <div className="mt-2 flex items-center justify-between px-1">
          <div className="flex gap-1.5">
            {["Record", "Tests"].map((t) => (
              <span
                key={t}
                className="rounded-md bg-slate-100 px-2 py-0.5 text-[0.6875rem] font-medium text-slate-500 dark:bg-white/5 dark:text-slate-400"
              >
                {t}
              </span>
            ))}
          </div>
          <span className="text-[0.6875rem] text-slate-400">
            Signed in as Riya · Acme
          </span>
        </div>
      </div>
    </PanelShell>
  );
}

// Greeting / empty state with quick-prompt chips.
function Greeting({ onPick }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-2 pt-6 text-center">
      <div className="relative mb-5">
        <span className="absolute inset-0 animate-pulse-ring rounded-full bg-brand-500/40" />
        <span className="relative grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-white shadow-glow">
          <Sparkles size={26} />
        </span>
      </div>
      <h2 className="text-base font-semibold text-slate-900 dark:text-white">
        How can I help you test?
      </h2>
      <p className="mt-1.5 max-w-[16rem] text-sm text-slate-500 dark:text-slate-400">
        Describe a test in plain English, or pick a quick prompt below.
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {CHIPS.map((chip) => (
          <button
            key={chip.id}
            onClick={() => onPick(chip.id)}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-all hover:border-brand-300 hover:text-brand-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:border-brand-500/40 dark:hover:text-brand-300"
          >
            <chip.icon size={14} />
            {chip.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function TypingBubble({ label }) {
  return (
    <div className="flex items-center gap-2 px-1 text-sm text-slate-500 dark:text-slate-400">
      <span className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="h-1.5 w-1.5 rounded-full bg-brand-500"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1, repeat: Infinity, delay: i * 0.18 }}
          />
        ))}
      </span>
      {label}
    </div>
  );
}

// The fixed-width "docked side panel" frame shared by both states.
function PanelShell({ children, onClose, mode, onCycleMode, onReset }) {
  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-slate-50 dark:bg-ink-950">
      <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-white/10">
        <Logo />
        <div className="flex items-center gap-1.5">
          {mode && (
            <button
              onClick={onCycleMode}
              title="Engine mode (set by your admin)"
              className="inline-flex items-center gap-1 rounded-lg bg-brand-500/10 px-2 py-1 text-[0.6875rem] font-semibold text-brand-600 transition-colors hover:bg-brand-500/20 dark:text-brand-300"
            >
              <Settings2 size={12} /> {mode}
            </button>
          )}
          {onReset && (
            <button
              onClick={onReset}
              aria-label="New test"
              title="New test"
              className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/5"
            >
              <RotateCcw size={15} />
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              aria-label="Close"
              className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/5"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </header>
      {children}
    </div>
  );
}
